// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useAuth, useUser } from '@/auth/clerkShim';
import { toast } from 'sonner';
import helpers from '@/utils/helpers';
import StripeSetupModal from '@/components/StripeSetupModal';
import {
  getOnboardingTemplates,
  ONBOARDING_SUPPORTED_SPORTS,
} from '@staty/shared/utils/onboardingTemplates';
import { buildRegistrationSeed } from '@staty/shared/utils/registrationSeed';
import { browserTimeZone } from '@/utils/registrationDateTz';
import StructureEditor, { cleanStructureDraft } from '@components/StructureEditor';

import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { publicSiteUrl, publicOrgOrigin } from '@/utils/publicSiteUrl';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import {
  Sparkles, Wand2, LayoutTemplate, Plus, Trash2, Loader2, Check, Rocket, ArrowRight,
  CreditCard, DollarSign, Link2, Copy, ExternalLink, PartyPopper, Code2, Users, CalendarDays,
  Palette,
} from 'lucide-react';
import { PageContainer } from '@/components/Page/PageContainer';
import EmbedCodeModal from '@/components/EmbedCodeModal/EmbedCodeModal';
import StripeConnectStatusCard from '@/components/StripeConnectStatus/StripeConnectStatusCard';
import useStripeConnectStatus from '@/hooks/useStripeConnectStatus';
import { ProgressRing } from '@/components/ui/progress-ring';
import { motion } from 'framer-motion';

import useOnboardingStore from '@/store/onboardingStore';
import useRegistrationStore from '@/store/registrationStore';
import usePaymentProductStore from '@/store/paymentProductStore';
import useTournamentStore from '@/store/tournamentStore';
import useLeagueStore from '@/store/leagueStore';
import useOrganizationStore from '@/store/organizationStore';
import useSummaryStore from '@/store/summaryStore';
import useDivisionStore from '@/store/divisionStore';
import { copyText } from '@/utils/shareLink';
import { tenantOrigin, localTenantOrigin } from '@staty/shared/domains';

const slugify = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

const publicRegistrationUrl = (org, registrationId) => {
  const slug = org?.home || org?.name;
  if (!slug || !registrationId) return '';
  const host = window.location.hostname;
  const isLocal = host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost');
  const base = isLocal ? localTenantOrigin(slug) : tenantOrigin(slug);
  return `${base}/register/details/${registrationId}`;
};

const StepBadge = ({ n, done }) => (
  <span
    className={`flex h-6 w-6 items-center justify-center rounded-full text-sm ${
      done ? 'bg-success text-success-foreground' : 'bg-primary text-primary-foreground'
    }`}
  >
    {done ? <Check className="h-4 w-4" /> : n}
  </span>
);

export default function GetStartedAdmin() {
  const { league: org } = useOutletContext() || {};
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { user } = useUser();

  const orgId = helpers.getObjId(org);
  const orgName = org?.name;
  const sport = (org?.sports?.[0] || '').toLowerCase();
  const sportSupported = ONBOARDING_SUPPORTED_SPORTS.includes(sport);
  const templates = useMemo(() => getOnboardingTemplates(sport), [sport]);
  // Live Connect status (ADR 0040): "connected" means charges_enabled, not
  // "an account exists". The hook refetches on tab refocus — onboarding runs
  // in a new tab. During a Stripe outage (error:'unavailable') fall back to
  // account-exists so a blip can't un-complete the checklist or block a fee.
  const { status: stripeStatus, loading: stripeStatusLoading, refetch: refetchStripeStatus } = useStripeConnectStatus(orgId);
  const stripeAccountExists = !!(stripeStatus?.hasAccount || org?.stepsTaken?.createdStripeAccount);
  const stripeChargesReady = !!stripeStatus?.chargesEnabled
    || (stripeStatus?.error === 'unavailable' && stripeAccountExists);

  const suggestStructure = useOnboardingStore((s) => s.suggestStructure(getToken));
  const buildStructure = useOnboardingStore((s) => s.buildStructure(getToken));

  const { registrations, getAllRegistrations, addRegistration, updateRegistration } = useRegistrationStore((s) => ({
    registrations: s.registrations,
    getAllRegistrations: s.getAllRegistrations,
    addRegistration: s.addRegistration(getToken),
    updateRegistration: s.updateRegistration(getToken),
  }));
  const createPaymentProduct = usePaymentProductStore((s) => s.createPaymentProduct(getToken));
  const { tournaments, getTournamentsByLeagueName } = useTournamentStore((s) => ({
    tournaments: s.tournaments,
    getTournamentsByLeagueName: s.getTournamentsByLeagueName,
  }));
  const { leagues, getLeaguesByOrganizationName } = useLeagueStore((s) => ({
    leagues: s.leagues,
    getLeaguesByOrganizationName: s.getLeaguesByOrganizationName,
  }));
  const markOrgStep = useOrganizationStore((s) => s.markOrgStep(getToken));
  const markLinkShared = useOrganizationStore((s) => s.markLinkShared(getToken));
  const { divisions, getDivisionsByLeagueName } = useDivisionStore((s) => ({
    divisions: s.divisions,
    getDivisionsByLeagueName: s.getDivisionsByLeagueName,
  }));
  const { setup, getSetup } = useSummaryStore((s) => ({ setup: s.setup, getSetup: s.getSetup(getToken) }));

  // ---- Step 1: customize / build-another ---------------------------------
  const [structure, setStructure] = useState(null); // { name, contestType, contests[], divisions[] }
  const [buildingAnother, setBuildingAnother] = useState(false); // review card → template/AI builder
  const [freeText, setFreeText] = useState('');
  // Landing-page AI teaser carry-through: the staty.io "describe your league"
  // widget stashes the visitor's description (sign-up page → localStorage);
  // pick it up ONCE, open the builder on the AI tab pre-filled, so the thing
  // they previewed pre-signup is one click from real.
  const [builderTab, setBuilderTab] = useState('template');
  useEffect(() => {
    try {
      const pending = localStorage.getItem('staty-pending-league-desc');
      if (!pending) return;
      localStorage.removeItem('staty-pending-league-desc');
      localStorage.removeItem('staty-pending-league-sport');
      // INT-0011 tier 2: the carried structure key rides with them — the
      // wizard's confirm step consumes all three on a successful build, so
      // reaching here means a degrade path; clean up the stragglers.
      localStorage.removeItem('staty-pending-league-structure');
      setFreeText(pending);
      setBuilderTab('ai');
      setBuildingAnother(true);
    } catch { /* storage unavailable — skip */ }
  }, []);
  const [generating, setGenerating] = useState(false);
  const [building, setBuilding] = useState(false);
  const [built, setBuilt] = useState(!!org?.stepsTaken?.builtFirstStructure);

  // "Looks good" — optimistic local mirror of the ADR 0038 customizedCompetition
  // stamp. (The registration review gate is gone, INT-0028: the link shows
  // immediately; reviewedRegistration is now stamped only by a real edit.)
  const [looksGoodComp, setLooksGoodComp] = useState(false);
  // INT-0028: optimistic mirror of funnelMarkers.linkSharedAt for this session.
  const [sharedThisSession, setSharedThisSession] = useState(false);

  // ---- Steps 2–5 ---------------------------------------------------------
  const [selectedCompId, setSelectedCompId] = useState('');
  const [creatingReg, setCreatingReg] = useState(false);
  const [createdReg, setCreatedReg] = useState(null);

  const [feeAmount, setFeeAmount] = useState('');
  const [feePer, setFeePer] = useState('team');
  const [savingFee, setSavingFee] = useState(false);
  const [feeDone, setFeeDone] = useState(false);
  const [embedOpen, setEmbedOpen] = useState(false);

  // Competition list = tournaments + standalone seasons for this org.
  const seasons = useMemo(() => (leagues || []).filter((l) => l.contestType === 'season'), [leagues]);
  const competitions = useMemo(
    () => [
      ...(tournaments || []).map((t) => ({ id: helpers.getObjId(t), label: t.name, kind: 'tournament' })),
      ...seasons.map((s) => ({ id: helpers.getObjId(s), label: s.name, kind: 'season' })),
    ],
    [tournaments, seasons],
  );

  const selectedComp = competitions.find((c) => c.id === selectedCompId) || null;

  const findRegForComp = (comp) =>
    comp &&
    (registrations || []).find((r) =>
      comp.kind === 'tournament'
        ? helpers.getObjId(r.tournament) === comp.id
        : helpers.getObjId(r.contest) === comp.id,
    );

  // Load org competitions + existing registrations so the page reflects reality
  // across reloads (not just what was built this session).
  useEffect(() => {
    if (!orgName) return;
    getTournamentsByLeagueName({ leagueName: orgName });
    getLeaguesByOrganizationName(orgName);
    getAllRegistrations(orgName);
    getDivisionsByLeagueName(orgName);
    if (orgId) getSetup(orgId);
  }, [orgName]);

  // INT-0028 "Add your teams and schedule": the first division of the
  // selected competition (lowest priority = first rendered) is where the
  // bootstrap seeded the example teams, so "Add teams" lands there.
  const firstDivision = useMemo(() => {
    if (!selectedComp) return null;
    // A division's `league` is its contest: the season itself, or one of the
    // tournament's contests (contestType 'tournament', league.tournament set).
    const contestIds = selectedComp.kind === 'tournament'
      ? (leagues || []).filter((l) => helpers.getObjId(l.tournament) === selectedComp.id).map((l) => helpers.getObjId(l))
      : [selectedComp.id];
    const mine = (divisions || []).filter((d) => contestIds.includes(helpers.getObjId(d.league)));
    mine.sort((a, b) => (Number(a.priority ?? 0) - Number(b.priority ?? 0)) || String(a.name || '').localeCompare(String(b.name || '')));
    return mine[0] || null;
  }, [divisions, leagues, selectedComp]);

  // Default the competition selection once competitions load.
  useEffect(() => {
    if (!selectedCompId && competitions.length) setSelectedCompId(competitions[0].id);
  }, [competitions, selectedCompId]);

  // Reflect an already-open registration for the chosen competition.
  useEffect(() => {
    const existing = findRegForComp(selectedComp);
    setCreatedReg(existing || null);
    setFeeDone(existing ? !!existing.paymentProduct : false);
  }, [selectedCompId, registrations]);

  const pickTemplate = (t) => {
    setStructure({
      name: t.name,
      contestType: t.contestType,
      contests: [...(t.contests || [])],
      divisions: t.divisions.map((d) => ({ ...d })),
    });
  };

  const generate = async () => {
    if (!freeText.trim() || generating) return;
    setGenerating(true);
    try {
      const result = await suggestStructure(orgId, freeText.trim());
      setStructure({
        name: result.name || 'My Competition',
        contestType: result.contestType === 'tournament' ? 'tournament' : 'season',
        contests: Array.isArray(result.contests) ? result.contests : [],
        divisions: Array.isArray(result.divisions) && result.divisions.length
          ? result.divisions
          : [{ name: 'Open Division', format: 'round-robin', capacity: 8 }], // capacity 8: FORM SEED, not a platform default (ADR 0035)
      });
    } catch (e) {
      toast.error(e?.message || 'Could not generate a setup. Try rephrasing.');
    } finally {
      setGenerating(false);
    }
  };

  const create = async () => {
    if (building) return;
    const clean = cleanStructureDraft(structure);
    if (!clean.divisions.length) {
      toast.error('Add at least one division.');
      return;
    }
    setBuilding(true);
    try {
      // ONE round-trip: structure + its registration (ADR 0037/0038).
      const res = await buildStructure(orgId, {
        ...clean,
        registration: { open: true, timeZone: browserTimeZone() },
      });
      setBuilt(true);
      setStructure(null);
      setBuildingAnother(false);
      if (res?.registrationError) {
        toast.warning(`${clean.name} created, but its registration could not be created. Open one below.`);
      } else {
        toast.success(`${clean.name} created with ${clean.divisions.length} division${clean.divisions.length > 1 ? 's' : ''}. Registration is open.`);
      }
      // Refresh competitions + registrations and preselect the one just built.
      await Promise.all([
        getTournamentsByLeagueName({ leagueName: orgName }),
        getLeaguesByOrganizationName(orgName),
        getAllRegistrations(orgName),
      ]);
      const newId = helpers.getObjId(res?.tournamentId || res?.leagueId);
      if (newId) setSelectedCompId(newId);
    } catch (e) {
      toast.error(e?.message || 'Could not create your structure.');
    } finally {
      setBuilding(false);
    }
  };

  // "Looks good" — stamp a review step done (optimistic; reverted on failure).
  const confirmStep = async (step) => {
    const setLocal = setLooksGoodComp;
    setLocal(true);
    const res = await markOrgStep(orgId, step);
    if (res) getSetup(orgId);
    else setLocal(false);
  };

  // ---- Attach path: open a registration for an EXISTING competition that
  // has none (legacy orgs / structure-only builds). Roster limits are edited
  // on the registration edit page now — no maxPlayers seed here.
  const buildRegPayload = (comp) => ({
    ...(comp.kind === 'tournament'
      ? { tournament: comp.id, contestType: 'tournament' }
      : { contest: comp.id, contestType: 'season', seasonName: comp.label, fieldId: slugify(comp.label) }),
    sports: sport ? [sport] : [],
    organization: orgId,
    league: orgId,
    timeZone: browserTimeZone(),
    // ONE defaults factory for every registration-create surface (ADR 0037).
    // Get Started's deliberate divergence rides in as an override: minPlayers 1.
    ...buildRegistrationSeed({
      sport,
      overrides: {
        minPlayers: 1, // FORM SEED, not a platform default (ADR 0035)
      },
    }),
    active: true, // live immediately from Get Started
  });

  const openRegistration = async () => {
    if (creatingReg) return;
    if (!selectedComp) {
      toast.error('Pick a competition first.');
      return;
    }
    const existing = findRegForComp(selectedComp);
    if (existing) {
      setCreatedReg(existing);
      toast.success('Registration is already open for this competition.');
      return;
    }
    setCreatingReg(true);
    let created = null;
    await addRegistration(buildRegPayload(selectedComp), (r) => { created = r; });
    setCreatingReg(false);
    if (created?._id) {
      setCreatedReg(created);
      getAllRegistrations(orgName);
      toast.success('Registration is live.');
    } else {
      toast.error('Could not open registration.');
      // A 409 means one already exists but our list is stale — re-sync so
      // findRegForComp picks it up and the step self-heals.
      getAllRegistrations(orgName);
    }
  };

  // ---- Step 3: set a fee & get paid (fee + inline Stripe) ----------------
  const saveFee = async () => {
    if (savingFee) return;
    if (!createdReg) {
      toast.error('Open registration first.');
      return;
    }
    const amt = Number(feeAmount);
    if (!amt || amt <= 0) {
      toast.error('Enter a fee amount.');
      return;
    }
    // Fees are live-only (Test Mode retired — ADR 0045): a fully-enabled
    // Stripe account is required before any fee can be attached.
    if (!stripeChargesReady) {
      toast.error('Finish payment setup first to take payments.');
      return;
    }
    setSavingFee(true);
    try {
      const product = await createPaymentProduct({
        name: `${createdReg.seasonName || selectedComp?.label || 'Team'} Registration`,
        amount: Math.round(amt * 100), // cents — the create endpoint unified onto cents 2026-08-26
        perTeam: feePer === 'team',
        perPlayer: feePer === 'player',
        league: orgId,
        isFree: false,
      });
      if (!product?._id) throw new Error('no product');
      let updated = null;
      await updateRegistration(
        {
          _id: createdReg._id,
          // `paymentProduct` is what actually enables the fee; a `collectPayments`
          // key here never persisted (not on RegistrationSchema).
          paymentProduct: product._id,
        },
        (r) => { updated = r; },
      );
      if (updated) setCreatedReg(updated);
      setFeeDone(true);
      getAllRegistrations(orgName);
      toast.success('Fee added to registration.');
    } catch (e) {
      toast.error('Could not save the fee.');
    } finally {
      setSavingFee(false);
    }
  };

  // Stripe Connect — EMBEDDED onboarding in a modal (ADR 0044); no redirect.
  const [stripeModalOpen, setStripeModalOpen] = useState(false);
  const connectStripe = () => setStripeModalOpen(true);

  const live = setup || {};
  const publicUrl = createdReg ? publicRegistrationUrl(org, helpers.getObjId(createdReg)) : '';
  // INT-0049: the share card hands out the SCHEDULE link once games exist,
  // else the site itself; the registration link lives on the optional card.
  const siteUrl = org ? publicOrgOrigin(org, org.name) : '';
  const scheduleUrl = (() => {
    const d = live.firstRealDivision;
    if (!d || !d.hasGames || !org) return '';
    const path = d.contestType === 'tournament' && d.tournamentId
      ? `/tournament/${helpers.getObjId(d.tournamentId)}/division/${helpers.getObjId(d.divisionId)}`
      : `/season/${helpers.getObjId(d.leagueId)}/division/${helpers.getObjId(d.divisionId)}`;
    return publicSiteUrl(org.name, path);
  })();
  const shareUrl = scheduleUrl || siteUrl;
  const copyAnyLink = async (url: string, label: string) => {
    if (await copyText(url)) {
      toast.success(`${label} copied.`);
      // INT-0028 set-once marker; the server claim is idempotent.
      if (!sharedThisSession) {
        setSharedThisSession(true);
        Promise.resolve(markLinkShared(orgId)).then(() => { if (orgId) getSetup(orgId); }).catch(() => {});
      }
    } else {
      toast.error('Could not copy. Select and copy manually.');
    }
  };
  const copyLink = () => copyAnyLink(shareUrl, scheduleUrl ? 'Schedule link' : 'Site link');
  const copyRegistrationLink = () => copyAnyLink(publicUrl, 'Registration link');

  // Overall progress — blend live /summary/setup with this-session local state so
  // fresh actions count immediately (ADR 0038: review-checklist semantics).
  // Bootstrap guarantees every new org a structure AND a registration, so
  // existence flags (hasTournaments/hasSeasons/hasRegistration) are useless as
  // progress. Done = the admin either EDITED the thing (server-stamped
  // customizedCompetition/reviewedRegistration), confirmed it ("Looks good"),
  // or deliberately built their own (builtFirstStructure — legacy credit).
  const customizedDone = looksGoodComp || built || !!live.customizedCompetition || !!live.builtFirstStructure;
  // INT-0028: the link is the first step — done once it has been copied,
  // shared, or emailed (funnelMarkers.linkSharedAt via summary/setup).
  const shareDone = sharedThisSession || !!live.linkSharedAt;
  // "Add your teams and schedule" = at least one game exists. Team counts
  // can't serve: bootstrap seeds example teams into every new org.
  const teamsScheduleDone = (live.gamesCount || 0) > 0;
  // INT-0043: the wizard's drafted schedule ships without game times, so the
  // card's next action becomes "Set game times" until the scheduler has them.
  const scheduleNeedsTimes = teamsScheduleDone && (live.gamesWithoutTime || 0) > 0;
  const feeStepDone = feeDone || !!live.hasPaymentProducts;
  // Done = Stripe can actually charge (owner decision 2026-08-26) — an
  // account mid-onboarding shows as in-progress, not done.
  const stripeStepDone = stripeChargesReady;
  // One combined money step: setting a fee OR connecting Stripe both count —
  // each is optional monetization progress (test fees need no Stripe).
  const moneyStepDone = feeStepDone || stripeStepDone;
  const totalSteps = 4;
  const stepsDone = [shareDone, teamsScheduleDone, customizedDone, moneyStepDone].filter(Boolean).length;
  const pct = Math.round((stepsDone / totalSteps) * 100);
  // INT-0049: "live" = the organizer's job is done AND out there (schedule
  // published + a link shared); registration is the optional last card.
  const isLive = shareDone && teamsScheduleDone;

  return (
    <PageContainer>
      {/* Hero — progress ring + "you're live" celebration */}
      <div className="flex items-center justify-between gap-4 rounded-xl border bg-muted/30 p-5">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-semibold tracking-tight">Get Started</h1>
          </div>
          {isLive ? (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="flex items-center gap-2 text-sm font-medium text-success"
            >
              <PartyPopper className="h-5 w-5" /> You&apos;re live! Your schedule is out there.
            </motion.div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Your site is live. Add your teams, publish the schedule, and share it. Registration
              and fees come whenever you need them.
            </p>
          )}
          <p className="text-xs text-muted-foreground">{stepsDone} of {totalSteps} steps done</p>
        </div>
        <div className="h-16 w-16 shrink-0">
          <ProgressRing
            value={pct}
            label={isLive ? '✓' : `${pct}%`}
            tone={isLive ? 'success' : 'primary'}
          />
        </div>
      </div>

      {/* Working-on bar — both review steps operate on this competition */}
      {competitions.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-muted/20 px-4 py-3">
          <Label className="text-sm shrink-0">Working on</Label>
          <div className="min-w-[240px] flex-1 sm:max-w-sm">
            <Select value={selectedCompId} onValueChange={setSelectedCompId}>
              <SelectTrigger><SelectValue placeholder="Pick a competition" /></SelectTrigger>
              <SelectContent>
                {competitions.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.label} · {c.kind === 'tournament' ? 'Tournament' : 'League'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="ghost"
            size="sm"
            data-tour="get-started-edit-competitions"
            onClick={() => navigate('/admin/organize')}
          >
            Edit in Competitions <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}

      {/* Step 1 — teams + schedule (INT-0049: the organizer's job comes first) (INT-0028: retained organizers are the ones
          running a season NOW; the bootstrap division already holds example
          teams and INT-0027 made game #1 zero-setup) */}
      <Card className={competitions.length ? '' : 'opacity-60'}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <StepBadge n={1} done={teamsScheduleDone} />
            Add your teams and publish the schedule
          </CardTitle>
          <CardDescription>
            {teamsScheduleDone
              ? (scheduleNeedsTimes
                ? 'Your schedule is drafted. Set game times in the scheduler, then enter scores from the division page.'
                : 'Your schedule has started. Keep adding games and scores from the division page.')
              : 'Put your real teams in and publish the schedule. Standings and scores go live on your site as you enter them.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!competitions.length ? (
            <div className="text-sm text-muted-foreground">Create a competition in step 3 first.</div>
          ) : (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline" size="sm"
                data-tour="get-started-add-teams"
                onClick={() => navigate(firstDivision ? `/admin/division/${helpers.getObjId(firstDivision)}` : '/admin/organize')}
              >
                <Users className="h-4 w-4 mr-1" /> Add teams
              </Button>
              <Button
                variant="outline" size="sm"
                data-tour="get-started-build-schedule"
                onClick={() => {
                  // INT-0027: "Set game times" opens the competition's scheduler
                  // with the quick-availability wizard already open.
                  const d = live.firstRealDivision;
                  if (scheduleNeedsTimes && d) {
                    navigate(d.contestType === 'tournament' && d.tournamentId
                      ? `/admin/scheduler-day2/tournament/${helpers.getObjId(d.tournamentId)}?quick=1`
                      : `/admin/organization/scheduler/${helpers.getObjId(d.leagueId)}?quick=1`);
                    return;
                  }
                  navigate('/admin/scheduler');
                }}
              >
                <CalendarDays className="h-4 w-4 mr-1" /> {scheduleNeedsTimes ? 'Set game times' : 'Build the schedule'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step 2 — share your site (INT-0049): the schedule link once games
          exist, else the site; any copy stamps the set-once linkSharedAt. */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <StepBadge n={2} done={shareDone} />
            Share your site
          </CardTitle>
          <CardDescription>
            {scheduleUrl
              ? 'Your schedule is on your site. Send this link to your teams so everyone knows who plays when.'
              : 'Your site is live. Share it now, and the schedule shows up there as soon as you publish it.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            <Link2 className="h-4 w-4 text-muted-foreground shrink-0" />
            <Input readOnly value={shareUrl} aria-label={scheduleUrl ? 'Schedule link' : 'Site link'} className="font-mono text-xs" />
            <Button variant="outline" size="icon" aria-label="Copy link" onClick={copyLink} title="Copy link" data-tour="get-started-copy-link">
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => window.open(shareUrl, '_blank')} data-tour="get-started-see-site">
              <ExternalLink className="h-4 w-4 mr-1" /> {scheduleUrl ? 'Open the schedule' : 'See your site'}
            </Button>
            {/* INT-0050: design left the wizard; this is where the look gets customized. */}
            <Button variant="outline" size="sm" onClick={() => navigate('/admin/appearance')} data-tour="get-started-customize-look">
              <Palette className="h-4 w-4 mr-1" /> Customize your look
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Step 3 — customize the competition (bootstrap made one; edit or confirm it) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <StepBadge n={3} done={customizedDone} />
            Customize your competition
          </CardTitle>
          <CardDescription>
            We created a sample {sportSupported ? sport : ''} competition with divisions for you.
            Rename it, adjust the divisions, or build your own.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!buildingAnother && !structure && competitions.length > 0 ? (
            <div className="rounded-md border bg-muted/30 p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm">
                <span className="font-medium">{selectedComp?.label || 'Your competition'}</span>
                {selectedComp ? ` · ${selectedComp.kind === 'tournament' ? 'Tournament' : 'League'}` : ''}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => navigate('/admin/organize')}>
                  Review &amp; edit <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
                {!customizedDone && (
                  <Button size="sm" onClick={() => confirmStep('customizedCompetition')}>
                    <Check className="h-4 w-4 mr-1" /> Looks good
                  </Button>
                )}
                {sportSupported && (
                  <Button variant="ghost" size="sm" onClick={() => setBuildingAnother(true)}>
                    Build another
                  </Button>
                )}
              </div>
            </div>
          ) : !sportSupported ? (
            <div className="text-sm text-muted-foreground">
              Guided setup currently supports basketball, pickleball, volleyball, and soccer. You can still create leagues
              from <a className="text-primary underline" href="/admin/organize">Competitions</a>.
            </div>
          ) : !structure ? (
            <Tabs value={builderTab} onValueChange={setBuilderTab}>
              <TabsList>
                <TabsTrigger value="template"><LayoutTemplate className="h-4 w-4 mr-2" />Use a template</TabsTrigger>
                <TabsTrigger value="ai"><Wand2 className="h-4 w-4 mr-2" />Describe it</TabsTrigger>
              </TabsList>
              <TabsContent value="template" className="mt-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  {templates.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => pickTemplate(t)}
                      className="text-left rounded-lg border p-4 hover:border-primary hover:bg-accent transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="font-medium">{t.label}</div>
                        <Badge variant="secondary">{t.contestType === 'tournament' ? 'Tournament' : 'League'}</Badge>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">{t.description}</div>
                      <div className="text-xs text-muted-foreground mt-2">
                        {t.divisions.length} division{t.divisions.length > 1 ? 's' : ''}
                        {t.contests?.length ? ` · ${t.contests.length} contests` : ''}
                      </div>
                    </button>
                  ))}
                </div>
              </TabsContent>
              <TabsContent value="ai" className="mt-4 space-y-2">
                <Label className="text-sm">Describe your league or tournament</Label>
                <Textarea
                  placeholder={{
                    pickleball: 'e.g. A weekend mixed-doubles tournament with 3.0, 3.5 and 4.0+ divisions, about 16 teams',
                    volleyball: 'e.g. A coed rec league with A, B and C divisions, 8 teams each',
                    soccer: 'e.g. A youth club with U10, U12 and U14 divisions, 8 teams each',
                  }[sport] || 'e.g. A rec league with Upper, Lower and Open divisions, 8 teams each'}
                  value={freeText}
                  onChange={(e) => setFreeText(e.target.value)}
                  rows={3}
                />
                <div className="flex justify-end">
                  <Button onClick={generate} disabled={!freeText.trim() || generating}>
                    {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wand2 className="h-4 w-4 mr-2" />}
                    Generate
                  </Button>
                </div>
              </TabsContent>
              {competitions.length > 0 && (
                <div className="mt-3">
                  <Button variant="ghost" size="sm" onClick={() => setBuildingAnother(false)}>Back</Button>
                </div>
              )}
            </Tabs>
          ) : (
            // Review & confirm editor (shared with CreateCompetitionDialog)
            <div className="space-y-4">
              <StructureEditor value={structure} onChange={setStructure} showTypeSelect />

              <div className="flex justify-between">
                <Button variant="ghost" onClick={() => setStructure(null)}>Back</Button>
                <Button onClick={create} disabled={building}>
                  {building ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Rocket className="h-4 w-4 mr-2" />}
                  Create it
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step 4 — registration + fee + Stripe (INT-0049: optional, last).
          Signup is open and free from the bootstrap; the link, the fee form
          and the Connect action all live here. */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <StepBadge n={4} done={moneyStepDone} />
            Open registration and set a fee <span className="text-xs font-normal text-muted-foreground">(optional)</span>
          </CardTitle>
          <CardDescription>
            Signup is already open and free. Share the registration link when you need teams to
            sign up, and add a fee and payments when you want to collect.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {!competitions.length ? (
            <div className="text-sm text-muted-foreground">Create a competition in step 3 first.</div>
          ) : !selectedComp ? (
            <div className="text-sm text-muted-foreground">Pick a competition above.</div>
          ) : createdReg ? (
            <>
              <div className="flex items-center gap-2">
                <Link2 className="h-4 w-4 text-muted-foreground shrink-0" />
                <Input readOnly value={publicUrl} aria-label="Registration link" className="font-mono text-xs" />
                <Button variant="outline" size="icon" aria-label="Copy registration link" onClick={copyRegistrationLink} title="Copy registration link" data-tour="get-started-copy-registration-link">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => window.open(publicUrl, '_blank')}>
                  <ExternalLink className="h-4 w-4 mr-1" /> Preview registration
                </Button>
                <Button variant="outline" size="sm" onClick={() => setEmbedOpen(true)}>
                  <Code2 className="h-4 w-4 mr-1" /> Embed on your site
                </Button>
                <Button
                  variant="outline" size="sm"
                  data-tour="get-started-review-registration"
                  onClick={() => navigate(`/admin/registration/${helpers.getObjId(createdReg)}/edit`)}
                >
                  Review your registration <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
                <Button size="sm" onClick={() => navigate(`/admin/registration/${helpers.getObjId(createdReg)}`)}>
                  Manage registration <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{selectedComp?.label}</span> has no
                registration yet. Open one with sensible defaults (free until you add a fee).
              </p>
              <Button onClick={openRegistration} disabled={creatingReg}>
                {creatingReg ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Rocket className="h-4 w-4 mr-2" />}
                Open registration
              </Button>
            </div>
          )}
          <div className="border-t pt-4 space-y-4">
          {!createdReg ? (
            <div className="text-sm text-muted-foreground">Open registration above to set a fee.</div>
          ) : feeDone ? (
            <div className="rounded-md border bg-muted/30 p-3 flex items-center gap-2 text-sm">
              <Check className="h-4 w-4 text-success" />
              A fee is attached to this registration.
            </div>
          ) : (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label className="text-sm">Amount (USD)</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="text" inputMode="decimal" className="pl-7"
                      placeholder="0.00"
                      value={feeAmount}
                      onChange={(e) => setFeeAmount(e.target.value.replace(/[^0-9.]/g, ''))}
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">Charged</Label>
                  <Select value={feePer} onValueChange={setFeePer}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="team">Per team</SelectItem>
                      <SelectItem value="player">Per player</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {!stripeChargesReady && (
                <p className="text-xs text-warning">
                  Payments need to be fully enabled. Finish payment setup below, then save this fee.
                </p>
              )}
              <div className="flex justify-end">
                <Button onClick={saveFee} disabled={savingFee || !stripeChargesReady}>
                  {savingFee ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <DollarSign className="h-4 w-4 mr-2" />}
                  Save fee
                </Button>
              </div>
            </>
          )}

          {/* Stripe — always visible (doesn't need a registration). Shows the
              REAL three-state status; Continue resumes the same account. */}
          <div className="border-t pt-4">
            <StripeConnectStatusCard
              status={stripeStatus}
              loading={stripeStatusLoading}
              onConnect={connectStripe}
              notConnectedHint="Set up payments to charge registration fees. Free registrations work without it."
            />
          </div>
          </div>
        </CardContent>
      </Card>

      {createdReg && (
        <EmbedCodeModal
          open={embedOpen}
          onOpenChange={setEmbedOpen}
          registrationId={helpers.getObjId(createdReg)}
          orgSlug={org?.home || org?.name}
        />
      )}
      <StripeSetupModal
        open={stripeModalOpen}
        onOpenChange={setStripeModalOpen}
        orgId={orgId}
        onComplete={refetchStripeStatus}
      />
    </PageContainer>
  );
}
